import { useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/api/client"
import { fehlerNachricht, useToast } from "@/lib/toast"
import { useDocumentTitle } from "@/lib/useDocumentTitle"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"

interface Produktionsdaten {
  id: number
  produktionstag: string
  nitrierteMengeKg: string | null
  produzierteMengeChipsKg: string | null
}

function ProduktionsdatenSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [produktionstag, setProduktionstag] = useState(() => new Date().toISOString().slice(0, 10))
  const [nitrierteMengeKg, setNitrierteMengeKg] = useState("")
  const [produzierteMengeChipsKg, setProduzierteMengeChipsKg] = useState("")
  const [feldFehler, setFeldFehler] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setProduktionstag(new Date().toISOString().slice(0, 10))
    setNitrierteMengeKg("")
    setProduzierteMengeChipsKg("")
    setFeldFehler({})
  }, [open])

  const anlegen = useMutation({
    mutationFn: async () =>
      (
        await api.post("/produktionsdaten", {
          produktionstag,
          nitrierteMengeKg: nitrierteMengeKg || undefined,
          produzierteMengeChipsKg: produzierteMengeChipsKg || undefined,
        })
      ).data,
    onSuccess: () => {
      setFeldFehler({})
      queryClient.invalidateQueries({ queryKey: ["produktionsdaten"] })
      toast.success("Produktionsdaten gespeichert.")
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const nachricht = fehlerNachricht(err, "Konnte nicht gespeichert werden.")
      setFeldFehler({ form: nachricht })
      toast.error(nachricht, { retry: () => anlegen.mutate() })
    },
  })

  function absenden(e: React.FormEvent) {
    e.preventDefault()
    if (!produktionstag) {
      setFeldFehler({ produktionstag: "Pflichtfeld" })
      document.getElementById("produktionstag")?.focus()
      return
    }
    setFeldFehler({})
    anlegen.mutate()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="energie.produktionsdaten" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Produktionsdaten erfassen</SheetTitle>
          <SheetDescription>
            Tägliche Produktionskennzahlen (F5) — Energie-pro-Menge-KPI ist ein optionales
            Feature-Flag (FEATURE_ENERGIE_PRO_MENGE_KPI), siehe Dashboard.
          </SheetDescription>
        </SheetHeader>
        <form className="grid gap-4 py-4" onSubmit={absenden} noValidate>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="produktionstag">
              Produktionstag
            </label>
            <input
              id="produktionstag"
              type="date"
              value={produktionstag}
              onChange={(e) => setProduktionstag(e.target.value)}
              aria-invalid={!!feldFehler.produktionstag}
              aria-describedby={feldFehler.produktionstag ? "produktionstag-fehler" : undefined}
              className={`h-9 w-full rounded-md border bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 ${feldFehler.produktionstag ? "border-red-500" : "border-slate-300 focus:border-slate-400"}`}
            />
            {feldFehler.produktionstag && (
              <p id="produktionstag-fehler" className="mt-0.5 text-xs text-red-600">
                {feldFehler.produktionstag}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="nitrierteMenge">
              Nitrierte Menge (kg)
            </label>
            <input
              id="nitrierteMenge"
              type="number"
              step="0.01"
              value={nitrierteMengeKg}
              onChange={(e) => setNitrierteMengeKg(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm text-slate-600" htmlFor="produzierteChips">
              Produzierte Chips (kg)
            </label>
            <input
              id="produzierteChips"
              type="number"
              step="0.01"
              value={produzierteMengeChipsKg}
              onChange={(e) => setProduzierteMengeChipsKg(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60"
            />
          </div>
          {feldFehler.form && <p className="text-sm text-red-600">{feldFehler.form}</p>}
          <SheetFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={anlegen.isPending}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
            >
              {anlegen.isPending ? "Speichert…" : "Speichern"}
            </button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}

export function ProduktionsdatenPage() {
  useDocumentTitle("Produktionsdaten")
  const [sheetOffen, setSheetOffen] = useState(false)

  const query = useQuery({
    queryKey: ["produktionsdaten"],
    queryFn: async () => (await api.get<Produktionsdaten[]>("/produktionsdaten")).data,
  })

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Produktionsdaten</h1>
          <p className="mt-1 text-sm text-slate-500">
            Tägliche Produktionskennzahlen (F5) — Energie-pro-Menge-KPI ist ein optionales
            Feature-Flag (FEATURE_ENERGIE_PRO_MENGE_KPI), siehe Dashboard.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSheetOffen(true)}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          Produktionsdaten erfassen
        </button>
      </div>

      <ProduktionsdatenSheet open={sheetOffen} onOpenChange={setSheetOffen} />

      <div className="mt-6 overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-2 font-semibold">Tag</th>
              <th className="px-4 py-2 font-semibold">Nitrierte Menge (kg)</th>
              <th className="px-4 py-2 font-semibold">Chips (kg)</th>
            </tr>
          </thead>
          <tbody>
            {[...(query.data ?? [])].reverse().map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 text-slate-700">{p.produktionstag}</td>
                <td className="px-4 py-2.5 text-slate-700">{p.nitrierteMengeKg ?? "—"}</td>
                <td className="px-4 py-2.5 text-slate-700">{p.produzierteMengeChipsKg ?? "—"}</td>
              </tr>
            ))}
            {query.data?.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-slate-400">
                  Noch keine Produktionsdaten erfasst — mit „Produktionsdaten erfassen" den ersten
                  Tag anlegen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
